// E8LinearEquationSolveClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 8) - rownanie I stopnia z jedna niewiadoma.
// Postac: a + (b - x)/c = d(x - e).  x = (a*c + b + c*d*e) / (c*d + 1).

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function solve({ a, b, c, d, e }) {
  return (a * c + b + c * d * e) / (c * d + 1);
}

function buildData({ a, b, c, d, e, fixedOptions = null }) {
  const x = solve({ a, b, c, d, e });
  const eqLatex = `${a}+\\frac{${b}-x}{${c}}=${d}(x-${e})`;

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((opt, index) => ({ ...opt, label: LETTERS[index] }));
    correctIndex = fixedOptions.findIndex((opt) => opt.correct);
  } else {
    const distractors = [x - 1, x + 1, x + 2].filter((v) => v > 0 && v !== x);
    const ordered = shuffle([
      { value: x, latex: `${x}`, correct: true },
      ...distractors.slice(0, 3).map((v) => ({ value: v, latex: `${v}`, correct: false })),
    ]);
    options = ordered.map((opt, index) => ({ ...opt, label: LETTERS[index] }));
    correctIndex = ordered.findIndex((opt) => opt.correct);
  }

  return { a, b, c, d, e, x, eqLatex, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8LinearEquationSolveClosedTask",
    data: buildData({
      a: 5, b: 1, c: 2, d: 2, e: 1,
      fixedOptions: [
        { value: 2, latex: "2", correct: false },
        { value: 2.5, latex: "2\\frac{1}{2}", correct: false },
        { value: 3, latex: "3", correct: true },
        { value: 13 / 3, latex: "4\\frac{1}{3}", correct: false },
      ],
    }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 8 },
    tags: ["e8", "algebra", "rownania-liniowe"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let params;
  let x;
  do {
    params = {
      a: pick([3, 4, 5, 6]),
      b: pick([1, 2, 3]),
      c: pick([2, 3]),
      d: pick([2, 3]),
      e: pick([1, 2]),
    };
    x = solve(params);
  } while (!Number.isInteger(x) || x < 2 || x > 12);

  return {
    type: "E8LinearEquationSolveClosedTask",
    data: buildData(params),
    source: { variant: "random", basedOn: "E8_2025_CZE_8" },
    tags: ["e8", "algebra", "rownania-liniowe"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Dane jest równanie</p>
      <BlockMath math={data.eqLatex} />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Rozwiązaniem tego równania jest liczba</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={index}>
            <ChoiceOptionMath letter={LETTERS[index]} math={option.latex} display />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={data.options[data.correctIndex].latex} display />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={option.latex} display />;
}

export function renderExplanation({ data }) {
  const { a, b, c, d, e, x } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Krok 1.</strong> Mnożymy obie strony przez <InlineMath math={`${c}`} /> i porządkujemy:</p>
      <BlockMath math={`${a * c}+${b}-x=${c * d}(x-${e})`} />
      <BlockMath math={`${a * c + b}-x=${c * d}x-${c * d * e}`} />
      <p><strong>Krok 2.</strong> Przenosimy niewiadome na jedną stronę:</p>
      <BlockMath math={`${a * c + b + c * d * e}=${c * d + 1}x`} />
      <BlockMath math={`x=\\frac{${a * c + b + c * d * e}}{${c * d + 1}}=${x}`} />
      <p>Poprawna odpowiedź to <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.</p>
    </div>
  );
}
